import { useDeviceRatingCoverage } from '../../hooks/useFaultAnalysis.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { COVERAGE_BUCKET_COLORS } from '../../types/faultAnalysis.ts'
import type { UnratedDeviceItem } from '../../types/faultAnalysis.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { exportCsv } from '../../utils/csv.ts'

/** Plain-language problem labels for the CSV (copy register: no enum tokens). */
const WORKLIST_PROBLEM_LABELS: Record<UnratedDeviceItem['bucket'], string> = {
  parseable: 'rating parseable — backfill not yet run',
  missing: 'no usable rating on record',
  unplaced: 'not locatable on the feeder graph',
}

interface DeviceRatingCoverageCardProps {
  feederId: number
}

export function DeviceRatingCoverageCard({ feederId }: DeviceRatingCoverageCardProps) {
  const { data, isLoading, error } = useDeviceRatingCoverage(feederId)

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Checking device-rating coverage...</div>
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">
        {friendlyError(error, 'load device-rating coverage')} This is a request
        error, not an empty feeder.
      </div>
    )
  }

  if (!data) {
    return <div className="text-sm text-gray-400 p-4">No coverage data available</div>
  }

  // Fully rated ⟺ nothing sits in a non-rated bucket. Anything else is an
  // actionable worklist, never a dead end (spec §9): parseable is a backfill
  // action, missing is a cleanup action, unplaced is a geometry action.
  const worklistCount = data.parseable + data.missing + data.unplaced
  const hasDevices = data.total_devices > 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Device-Rating Coverage</h3>
        <span className="text-xs text-gray-500">{data.total_devices} devices</span>
      </div>
      <AsOfStamp computedAt={data.analysis_timestamp} />

      {!hasDevices ? (
        <div className="text-sm text-gray-400 p-2">No protective devices found on this feeder</div>
      ) : (
        <>
          {/* Coverage buckets */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: COVERAGE_BUCKET_COLORS.rated }}>
                {data.rated}
              </div>
              <div className="text-xs text-gray-500 mt-1">Rated</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: COVERAGE_BUCKET_COLORS.parseable }}>
                {data.parseable}
              </div>
              <div className="text-xs text-gray-500 mt-1">Parseable</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: COVERAGE_BUCKET_COLORS.missing }}>
                {data.missing}
              </div>
              <div className="text-xs text-gray-500 mt-1">Missing</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: COVERAGE_BUCKET_COLORS.unplaced }}>
                {data.unplaced}
              </div>
              <div className="text-xs text-gray-500 mt-1">Unplaced</div>
            </div>
          </div>

          {/* Worklist — actionable, never a dead end (spec §9). */}
          {worklistCount > 0 ? (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs font-semibold text-amber-800">
                  Rating-coverage worklist — these devices are not yet backed by a populated rating column.
                </div>
                <button
                  className="shrink-0 rounded border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                  onClick={() =>
                    exportCsv(
                      `unrated-devices-feeder-${feederId}.csv`,
                      data.worklist.map(w => ({
                        device_id: w.device_id,
                        name: w.device_name ?? '',
                        type: w.device_type,
                        gis_id: w.gis_id ?? '',
                        problem: WORKLIST_PROBLEM_LABELS[w.bucket],
                        rating_string: w.rating ?? '',
                      })),
                    )
                  }
                >
                  Export worklist (CSV)
                </button>
              </div>
              {data.parseable > 0 && (
                <div
                  className="text-xs text-amber-800"
                  title="Backfill script: scripts/populate_device_ratings.py"
                >
                  {data.parseable} devices have a readable rating that is not
                  loaded into the system yet — needs an engineering data load;
                  flag for the engineer.
                </div>
              )}
              {data.missing > 0 && (
                <div className="text-xs text-amber-800">
                  {data.missing} devices have an unparseable rating string — add to the rating-cleanup
                  worklist (never assessed as adequate).
                </div>
              )}
              {data.unplaced > 0 && (
                <div className="text-xs text-amber-800">
                  {data.unplaced} devices could not be located on the feeder graph — check their geometry.
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-xs text-green-800">
              <span className="font-semibold">All {data.rated} devices have a populated rating.</span>{' '}
              No coverage gaps to work.
            </div>
          )}

          {/* Non-worklist warnings render once per tab via WarningSummary
              (spec §3.1). */}

          {/* Per-device-type breakdown */}
          {data.items.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                By device type ({data.items.length})
              </h4>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                      <th className="pb-1 pr-2">Type</th>
                      <th className="pb-1 pr-2 text-right">Rated</th>
                      <th className="pb-1 pr-2 text-right">Parseable</th>
                      <th className="pb-1 pr-2 text-right">Missing</th>
                      <th className="pb-1 pr-2 text-right">Unplaced</th>
                      <th className="pb-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map(it => (
                      <tr key={it.device_type} className="border-b border-gray-50">
                        <td className="py-1 pr-2 text-gray-600 capitalize">
                          {it.device_type.replace(/_/g, ' ')}
                        </td>
                        <td className="py-1 pr-2 text-right" style={{ color: COVERAGE_BUCKET_COLORS.rated }}>
                          {it.rated}
                        </td>
                        <td className="py-1 pr-2 text-right" style={{ color: COVERAGE_BUCKET_COLORS.parseable }}>
                          {it.parseable}
                        </td>
                        <td className="py-1 pr-2 text-right" style={{ color: COVERAGE_BUCKET_COLORS.missing }}>
                          {it.missing}
                        </td>
                        <td className="py-1 pr-2 text-right" style={{ color: COVERAGE_BUCKET_COLORS.unplaced }}>
                          {it.unplaced}
                        </td>
                        <td className="py-1 text-right text-gray-700 font-medium">{it.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
